import { TESTIMONIALS } from 'src/pages/Home/components/TestimonialSection/consts'

import asurgentPolo from './assets/asurgent-pike-navy.webp'
import asurgentShirt from './assets/asurgent-skjorta-vit.webp'
import bergtornetCap from './assets/bergtornet-keps.webp'
import gordonVestFront from './assets/gordon-vast-fram.webp'
import gordonVestBack from './assets/gordon-vast-rygg.webp'
import rotcartHoodie from './assets/rotcart-hoodie.webp'
import treydTee from './assets/treyd-tshirt.webp'
import vetnioBackpack from './assets/vetnio-ryggsack-mo2600.webp'

/**
 * Customer projects from Rasmus's Drive folder ("Klayder – Testimonials
 * (kundsidan)"): the Kundoversikt_produkter overview, models confirmed by
 * Carmelo 2026-10-07. `description` is our own wording of what we made,
 * from that overview; the customer's own words come from the testimonials
 * below. Copy is { en, sv } because `16"` breaks t()'s `English || Svenska`
 * splitting.
 */
export const PREVIOUS_WORK_PROJECTS = [
  {
    id: 'gordon',
    customer: 'Gordon Delivery',
    description: {
      en: 'A company vest has to look right on the whole team, front and back. For Gordon Delivery we chose a black padded vest and paired two techniques: a clean embroidered logo on the left chest, and a large DTF print across the back that reads from a distance. All 15 vests were made from an approved factory proof and arrived before the agreed date.',
      sv: 'En företagsväst ska sitta rätt på hela teamet, både fram och bak. Till Gordon Delivery valde vi en svart vadderad väst och kombinerade två tekniker: en ren broderad logga på vänster bröst och ett stort DTF-tryck över ryggen som syns på håll. Alla 15 västar tillverkades efter en godkänd korrektur från fabriken och levererades före avtalad dag.',
    },
    product: { en: 'Padded vest, black', sv: 'Vadderad väst, svart' },
    decoration: {
      en: 'Embroidery on the left chest, DTF print on the back',
      sv: 'Brodyr vänster bröst, DTF-tryck rygg',
    },
    images: [gordonVestFront, gordonVestBack],
  },
  {
    id: 'asurgent',
    customer: 'Asurgent',
    description: {
      en: 'Asurgent wanted one look across the team, whether it is a polo day or a shirt day. We put together 130 navy polos and 20 white shirts from Tee Jays and gave both the same small embroidered emblem on the chest: a subtle, consistent finish that feels professional rather than promotional.',
      sv: 'Asurgent ville ha ett enhetligt uttryck i hela teamet, oavsett om det är pikédag eller skjortdag. Vi tog fram 130 marinblå pikéer och 20 vita skjortor från Tee Jays och gav båda samma lilla broderade emblem på bröstet: en diskret, genomgående finish som känns professionell snarare än reklamig.',
    },
    product: {
      en: 'Navy polo and white shirt (Tee Jays)',
      sv: 'Piké navy och skjorta vit (Tee Jays)',
    },
    decoration: {
      en: 'Small embroidered emblem on the chest',
      sv: 'Litet broderat emblem på bröstet',
    },
    images: [asurgentPolo, asurgentShirt],
  },
  {
    id: 'vetnio',
    customer: 'Vetnio',
    description: {
      en: 'Vetnio wanted a bag their team would actually carry every day. We found the right model, a black, water-repellent backpack for 16-inch laptops (MO2600) with a clean shape that suits the brand, and added the Vetnio logo. Practical merch that gets used, not left in a drawer.',
      sv: 'Vetnio ville ha en väska som teamet faktiskt bär varje dag. Vi hittade rätt modell, en svart, vattenavvisande ryggsäck för 16-tumsdatorer (MO2600) med en ren form som passar varumärket, och lade till Vetnios logga. Praktisk merch som används, inte blir liggande i en låda.',
    },
    product: {
      en: '16" laptop backpack, water-repellent, black',
      sv: 'Dataryggsäck 16", vattenavvisande, svart',
    },
    decoration: { en: 'With logo', sv: 'Med logga' },
    images: [vetnioBackpack],
  },
  {
    id: 'treyd',
    customer: 'Treyd',
    description: {
      en: 'Treyd wanted t-shirts with a clean, minimal look that fits their brand. We made 20 crewneck tees with the logo on the chest and a print on the back: simple, sharp and easy to wear, and delivered fast.',
      sv: 'Treyd ville ha t-shirts med en ren, minimalistisk look som passar varumärket. Vi gjorde 20 crewneck-t-shirts med loggan på bröstet och ett tryck på ryggen: enkla, skarpa och lätta att bära, och snabbt levererade.',
    },
    product: { en: 'Crewneck t-shirt', sv: 'Crewneck t-shirt' },
    decoration: {
      en: 'Logo on the chest, print on the back',
      sv: 'Logga bröst, tryck rygg',
    },
    images: [treydTee],
  },
  {
    id: 'bergtornet',
    customer: 'Bergtornet Stockholm',
    description: {
      en: 'Bergtornet Stockholm wanted caps that live up to the rest of their brand. We chose a low profile vintage cap and embroidered the logo in its own colours, a premium detail that holds up close. A small run of around ten caps, made with the same care as a large order.',
      sv: 'Bergtornet Stockholm ville ha kepsar som håller samma nivå som resten av varumärket. Vi valde en low profile vintage-keps och broderade loggan i dess egna färger, en premiumdetalj som håller även på nära håll. En liten upplaga på ett tiotal kepsar, gjord med samma omsorg som en stor order.',
    },
    product: { en: 'Low profile vintage cap', sv: 'Low profile vintage cap' },
    decoration: { en: 'Embroidery', sv: 'Brodyr' },
    images: [bergtornetCap],
  },
  {
    id: 'rotcart',
    customer: 'Rotcart',
    description: {
      en: 'Rotcart wanted hoodies people would actually choose to wear. We delivered hoodies carrying the Rotcart logo: comfortable everyday pieces that keep the brand visible long after the order is done.',
      sv: 'Rotcart ville ha hoodies som folk faktiskt vill ha på sig. Vi levererade hoodies med Rotcarts logga: bekväma vardagsplagg som håller varumärket synligt långt efter att ordern är klar.',
    },
    product: { en: 'Hoodies', sv: 'Hoodies' },
    // Decoration is not documented in the overview.
    decoration: null,
    images: [rotcartHoodie],
  },
] as const

export type Testimonial = {
  id: string | number
  quote: string
  name: string
  title: string
  image: string | null
  /** The PREVIOUS_WORK_PROJECTS entry this customer's words belong to. */
  projectId?: string
}

/**
 * Quotes approved for the website: section 1 of "Testimonials – citat och
 * förslag" (Asurgent reuses the live home page entry as-is), plus the section 3
 * quotes for Vetnio, Treyd, Bergtornet and Rotcart, approved by the customers.
 */
const APPROVED_TESTIMONIALS: Testimonial[] = [
  {
    id: 'gordon',
    quote:
      'Klayder were easy to work with, did a great job on our company vests and delivered before the agreed date! || Klayder var smidiga att jobba med, snyggt utförande av våra företagsvästar och leverans innan avsatt dag!',
    name: 'Helene Hägerstrand',
    title: 'Payroll & HR Coordinator, Gordon Delivery',
    image: null,
    projectId: 'gordon',
  },
  { ...TESTIMONIALS[0], projectId: 'asurgent' },
  {
    id: 'vetnio',
    quote:
      'We wanted a backpack that was clean, durable and built for everyday use. Klayder understood exactly what we were after and found the right model. Smooth all the way from the first chat to the finished product. || Vi ville ha en ryggsäck som var clean, tålig och funkade varje dag. Klayder förstod direkt vad vi var ute efter och hittade rätt modell. Smidigt hela vägen från första snacket till färdig produkt.',
    name: 'Arman Karégar',
    title: 'Founder, Vetnio || Grundare, Vetnio',
    image: null,
    projectId: 'vetnio',
  },
  {
    id: 'treyd',
    quote:
      'Klayder helped us with t-shirts for our sales team: logo on the chest, print on the back and a clean, minimalist look that fits our brand. Fast delivery and easy to work with. || Klayder hjälpte oss med t-shirts till vårt säljteam: logga på bröstet, tryck på ryggen och en ren, minimalistisk look som passar vårt varumärke. Snabb leverans och enkelt samarbete.',
    name: 'Elin Moritz',
    title: 'Marketing Director, Treyd',
    image: null,
    projectId: 'treyd',
  },
  {
    id: 'bergtornet',
    quote:
      'We wanted caps that match the standard of the rest of our brand. Klayder created the design for us and made the whole process easy. || Vi ville ha kepsar som håller samma nivå som resten av vår profil. Klayder tog fram designen åt oss och gjorde hela processen enkel.',
    name: 'Carl Linder',
    title: 'Bergtornet Stockholm',
    image: null,
    projectId: 'bergtornet',
  },
  {
    id: 'rotcart',
    quote:
      'Hoodies people actually want to wear. Personal service and smooth from the first meeting to delivery. || Hoodies som folk faktiskt vill ha på sig. Personlig service och smidigt från första mötet till leverans.',
    // Contact person not known yet (Alfred has the contact)
    name: 'Rotcart',
    title: '',
    image: null,
    projectId: 'rotcart',
  },
]

/**
 * Quotes still awaiting the customer's OK live in an untracked
 * `*.local.ts` file, so they reach the local dev server only and never the
 * repo or a deployed bundle (or its source maps). Without the file this is
 * empty.
 */
const LOCAL_REVIEW_TESTIMONIALS: Testimonial[] = import.meta.env.DEV
  ? Object.values(
      import.meta.glob<Testimonial[]>('./*.local.ts', {
        eager: true,
        import: 'LOCAL_REVIEW_TESTIMONIALS',
      })
    ).flat()
  : []

export const PREVIOUS_WORK_TESTIMONIALS: Testimonial[] = [
  ...APPROVED_TESTIMONIALS,
  ...LOCAL_REVIEW_TESTIMONIALS,
]

/**
 * Splits an `English || Svenska` string into its two halves. Pass them to
 * t(en, sv): one-argument t() also splits per sentence, which mixes the
 * languages when both halves start with the same word ("Hoodies ...").
 */
export const bilingual = (text: string): [string, string] => {
  const [en, sv] = text.split('||').map((part) => part.trim())
  return [en, sv || en]
}

export const getProjectTestimonial = (projectId: string) =>
  PREVIOUS_WORK_TESTIMONIALS.find((item) => item.projectId === projectId)
